import React, { type RefObject } from "react";

export type Term = { term: string; definition: string };
export type SectionLink = {
  sectionId: string;
  sectionName: string;
  firstPageId: string;
};
export type BatesLink = {
  batesNumber: string;
  pageId: string;
};

/**
 * Convert literal \n sequences (backslash + n) in API responses
 * to actual newline characters for proper Markdown rendering.
 */
export const normalizeLiteralNewlines = (content: string): string => {
  if (!content) return content;
  return content.replace(/\\n/g, "\n");
};

export const unwrapAllCodeBlocks = (content: string): string => {
  if (!content) return content;

  const lines = content.split("\n");
  const out: string[] = [];

  let inFence = false;
  let fenceMarker = "";
  let blockStartIdx = -1;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const fenceMatch = line.match(/^(\s*)(```|~~~)([a-z0-9-]*)\s*$/i);

    if (fenceMatch) {
      const marker = fenceMatch[2];
      if (!inFence) {
        inFence = true;
        fenceMarker = marker;
        blockStartIdx = out.length;
      } else if (marker === fenceMarker) {
        const blockLines = out.slice(blockStartIdx);
        const keptBefore = out.slice(0, blockStartIdx);
        out.length = 0;
        out.push(...keptBefore, ...blockLines);

        inFence = false;
        fenceMarker = "";
        blockStartIdx = -1;
      } else {
        out.push(line);
      }
      continue;
    }

    out.push(line);
  }

  return out.join("\n");
};
/**
 * Ensure markdown block-level markers (list items and headings) that appear
 * mid-line get proper newlines inserted before them.
 */
export const ensureMarkdownNewlines = (content: string): string => {
  if (!content) return content;

  let result = content;

  // Insert newline before unordered list markers (- * +)
  result = result.replace(/([.!?:;,)]|(?<!\w)\])(?!\n) ?([-*+] )/g, "$1\n$2");

  // Insert newline before ordered list markers (1. 2. etc.)
  result = result.replace(/([!?\]:;,)])(?!\n) ?(\d+\. )/g, "$1\n$2");
  result = result.replace(/(?<!\d)(\.)(?!\n) ?(\d+\. )/g, "$1\n$2");

  // After closing bold markers (**), insert newline before list markers
  result = result.replace(/(\*\*) ([-*+] )/g, "$1\n$2");
  result = result.replace(/(\*\*) (\d+\. )/g, "$1\n$2");

  result = result.replace(/([^\n]) +(#{1,6} )/g, "$1\n\n$2");

  return result;
};

export const ensureBoldHeadingSpacing = (content: string): string => {
  if (!content) return content;

  let result = content;

  result = result.replace(/(\*\*\w+(?:\s+\w+)+\*\*)([A-Z])/g, "$1\n\n$2");

  result = result.replace(
    /([.!?])[ \t]*(?:\n(?!\n))?[ \t]*((?:-\s*)?\*\*[A-Z][^*]*\*\*)/g,
    "$1\n\n$2"
  );

  result = result.replace(/\n{3,}/g, "\n\n");

  return result;
};

export const TERMS_PROSE_CLASS =
  "prose prose-sm dark:prose-invert font-sans normal-case";

/**
 * Event-delegation hook for source-map and bates-link clicks.
 */
export function useSourceMapDelegation(
  ref: RefObject<HTMLElement | null>,
  onSectionClick?: (sectionId: string, firstPageId: string) => void,
  onBatesClick?: (pageId: string) => void
) {
  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const handler = (e: MouseEvent) => {
      const target = e.target as Element;

      const sourceMapLink = target.closest<HTMLElement>("a.source-map-link");
      if (sourceMapLink && el.contains(sourceMapLink)) {
        e.preventDefault();
        const sectionId = sourceMapLink.getAttribute("data-section-id");
        const firstPageId =
          sourceMapLink.getAttribute("data-first-page-id") ?? "";
        if (sectionId) onSectionClick?.(sectionId, firstPageId);
        return;
      }

      const batesLink = target.closest<HTMLElement>("a.bates-link");
      if (batesLink && el.contains(batesLink)) {
        e.preventDefault();
        const pageId = batesLink.getAttribute("data-bates-page-id");
        if (pageId) onBatesClick?.(pageId);
        return;
      }

      const sectionLink = target.closest<HTMLElement>("a.section-link");
      if (sectionLink && el.contains(sectionLink)) {
        e.preventDefault();
        const sectionId = sectionLink.getAttribute("data-section-id");
        const firstPageId =
          sectionLink.getAttribute("data-first-page-id") ?? "";
        if (sectionId) onSectionClick?.(sectionId, firstPageId);
        return;
      }

      // Structured data accordion toggle
      const trailerHeader = target.closest<HTMLElement>(
        ".structured-data-trailer__header"
      );
      if (trailerHeader && el.contains(trailerHeader)) {
        const trailer = trailerHeader.closest(".structured-data-trailer");
        if (trailer) {
          trailer.classList.toggle("open");
        }
        return;
      }
    };

    el.addEventListener("click", handler);
    return () => el.removeEventListener("click", handler);
  }, [ref, onSectionClick, onBatesClick]);
}
